import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { EmployeeApi } from 'services';
import { isNil } from 'lodash';
import { toastMessages } from 'config/constants';
import { useCompany } from './CompanyContext';
import get from 'lodash/get';
import toast from 'react-hot-toast';
import useGenericContext from 'hooks/useGenericContext';

const EmployeeContext = createContext(undefined);
const EmployeeFunctionsContext = createContext(undefined);

function EmployeeProvider({ children }) {
  const { currentCompany } = useCompany();

  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(true);
  const [employeesSaving, setEmployeesSaving] = useState(false);

  const loadEmployees = useCallback(async () => {
    if (!currentCompany?.id) return;

    try {
      const { data } = await EmployeeApi.listEmployeesByCompanyId(currentCompany.id);
      const employeeList = data.map((e) => ({
        ...e,
        profilePictureURL: e.profilePictureURL ?? null,
      }));
      setEmployees(employeeList);
    } catch (e) {
      if (get(e, 'response.status') !== 401) toast.error(toastMessages.error.FETCH_EMPLOYEES);
    }

    setEmployeesLoading(false);
  }, [currentCompany?.id]);

  useEffect(() => {
    setEmployeesLoading(true);
    loadEmployees();
  }, [loadEmployees]);

  const autoCreateEmployee = useCallback(async () => {
    setEmployeesSaving(true);
    try {
      const { employee } = await EmployeeApi.autoCreateEmployee(currentCompany?.id);
      setEmployees((employees) => [...employees, employee]);

      setTimeout(loadEmployees);
      toast.success(toastMessages.success.CREATE_EMPLOYEE);
    } catch (_) {
      toast.error(toastMessages.error.REGISTER_CONFIG);
    }

    setEmployeesSaving(false);
  }, [currentCompany?.id, loadEmployees]);

  const createEmployee = useCallback(
    async (data, pictureData = null) => {
      if (isNil(currentCompany?.id)) return;

      setEmployeesSaving(true);
      try {
        const response = await EmployeeApi.createCompanyEmployee(currentCompany.id, data);
        let newEmployee = { ...data, id: response.data.id };

        let pictureUpdated = false;
        let error = false;
        if (pictureData) {
          const { data, url } = pictureData;
          try {
            await EmployeeApi.setPicture(newEmployee.id, data);
            newEmployee = { ...newEmployee, profilePictureURL: url };
            pictureUpdated = true;
          } catch (e) {
            error = true;
          }
        }

        const newEmployees = [...employees, newEmployee];
        setEmployees(newEmployees);
        setEmployeesSaving(false);

        setTimeout(loadEmployees);

        toast.success(toastMessages.success.CREATE_EMPLOYEE);
        if (pictureUpdated) toast.success(toastMessages.success.UPDATE_EMPLOYEE_PROFILE_PICTURE);
        else if (error) toast.error(toastMessages.error.UPDATE_EMPLOYEE_PROFILE_PICTURE);

        return { created: true, pictureUpdated, item: newEmployee };
      } catch (e) {
        toast.error(toastMessages.error.CREATE_EMPLOYEE);
        setEmployeesSaving(false);
        return { created: false, pictureUpdated: false, item: null };
      }
    },
    [currentCompany?.id, employees, loadEmployees],
  );

  const deleteEmployee = useCallback(
    async (id, body) => {
      setEmployeesSaving(true);

      try {
        const response = await EmployeeApi.deleteEmployee(id, body);

        // Manage conflict response
        if (response.status === 'CONFLICT') {
          return { conflict: true, deleted: false };
        }

        // Manage NO DELETE response
        if (response.status === 'NO_DELETE') {
          return { deleted: false };
        }

        const newEmployeeList = employees.filter((e) => e.id !== id);
        setEmployees(newEmployeeList);
        setEmployeesSaving(false);

        setTimeout(loadEmployees);

        toast.success(toastMessages.success.DELETE_EMPLOYEE);
        return { deleted: true };
      } catch (e) {
        setEmployeesSaving(false);
        toast.error(toastMessages.error.DELETE_EMPLOYEE);
        return { deleted: false };
      }
    },
    [employees, loadEmployees],
  );

  const editEmployee = useCallback(
    async (employee, data, pictureData = null) => {
      setEmployeesSaving(true);

      try {
        const response = await EmployeeApi.updateEmployee(employee.id, data);

        // Manage conflict response
        if (response.status === 'CONFLICT') {
          return { conflict: true, updated: false };
        }

        let newEmployee = { ...employee, ...data };
        let pictureUpdated = false;
        let error = false;
        if (pictureData) {
          const { data, url } = pictureData;
          try {
            await EmployeeApi.setPicture(newEmployee.id, data);
            newEmployee = { ...newEmployee, profilePictureURL: url };
            pictureUpdated = true;
          } catch (e) {
            error = true;
          }
        }

        const index = employees.findIndex((e) => e.id === employee.id);
        let newItems = [...employees];
        newItems.splice(index, 1, newEmployee);
        setEmployees(newItems);
        setEmployeesSaving(false);

        setTimeout(loadEmployees);

        toast.success(toastMessages.success.UPDATE_EMPLOYEE);
        if (pictureUpdated) toast.success(toastMessages.success.UPDATE_EMPLOYEE_PROFILE_PICTURE);
        else if (error) toast.error(toastMessages.error.UPDATE_EMPLOYEE_PROFILE_PICTURE);

        return { edited: true, pictureUpdated, item: newEmployee };
      } catch (e) {
        toast.error(toastMessages.error.UPDATE_EMPLOYEE);
        setEmployeesSaving(false);
        return { edited: false, pictureUpdated: false, item: null };
      }
    },
    [employees, loadEmployees],
  );

  const editEmployeePicture = useCallback(
    async (employeeId, data, fileURL) => {
      setEmployeesSaving(true);

      try {
        await EmployeeApi.setPicture(employeeId, data);

        const index = employees.findIndex((e) => e.id === employeeId);
        let newItems = [...employees];
        const newEmployee = { ...newItems[index], profilePictureURL: fileURL };
        newItems.splice(index, 1, newEmployee);
        setEmployees(newItems);

        setEmployeesSaving(false);
        toast.success(toastMessages.success.UPDATE_EMPLOYEE_PROFILE_PICTURE);
        return true;
      } catch (e) {
        setEmployeesSaving(false);
        toast.error(toastMessages.error.UPDATE_EMPLOYEE_PROFILE_PICTURE);
        return false;
      }
    },
    [employees],
  );

  const value = useMemo(
    () => ({
      employees,
      employeesLoading,
      employeesSaving,
    }),
    [employees, employeesLoading, employeesSaving],
  );

  const functions = useMemo(
    () => ({
      autoCreateEmployee,
      createEmployee,
      deleteEmployee,
      editEmployee,
      editEmployeePicture,
    }),
    [autoCreateEmployee, createEmployee, deleteEmployee, editEmployee, editEmployeePicture],
  );

  return (
    <EmployeeContext.Provider value={value}>
      <EmployeeFunctionsContext.Provider value={functions}>{children}</EmployeeFunctionsContext.Provider>
    </EmployeeContext.Provider>
  );
}

/**
 * Return the items values.
 * @return {{ employees: Object, employeesLoading: boolean }}
 */
function useEmployee() {
  return useGenericContext(EmployeeContext, 'useEmployee cannot be used outside of a EmployeeProvider component.');
}

/**
 * Return the functions manipulating items.
 * @return {{ editEmployee: Function }}
 */
function useEmployeeFunctions() {
  return useGenericContext(
    EmployeeFunctionsContext,
    'useEmployeeFunctions cannot be used outside of a EmployeeProvider component',
  );
}

export { EmployeeProvider, useEmployee, useEmployeeFunctions };
